import {
  FluidAlignment,
  FluidBackgroundColor,
  FluidKeyedObject,
  FluidOverflow,
  FluidSize,
  FluidStyleSize,
  FluidTheme,
  FluidThemeInterface,
  generateUniqueId,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  Prop,
  State,
} from '@stencil/core';

import { FULL_HEIGHT_DEFAULT } from '../../constants/constants';
import { parseSizeValue } from '../../utils/formatting/formatting.utils';
import { Utils } from '../../utils/utils';
import { ContainerNamedSlots } from './components/fluid-container.components';

/**
 * @displayName Container
 *
 * @contextData FLUID Container is exactly what it sounds like - a multipurpose container for in-page content. Although it may sound basic, FLUID Container has many powerful features that some would consider to be the "Swiss Army Knife" of FLUID components.
 *
 * FLUID Container can be used to set padding, margin, horizontal/vertical scrolling, etc. anywhere it's needed. It's extremely flexible size-based Props accept values for pre-made "t-shirt sizes" (FluidSize), percentages (decimal numbers), pixel values (integers), or custom CSS (strings). It also has built-in named Slots for "fixed" content (i.e. "sticky" or "absolutely-positioned" content) on each side of the Container that will "anchor" content in place, even when scrolling. Use FLUID Container within FLUID Grid to tackle just about any content layout imaginable.
 *
 * @slot - Content displayed within the Container
 * @slot fixed-content-top - Named slot for fixed/sticky content. Spans 100% width across the top of the Container
 * @slot fixed-content-bottom - Named slot for fixed/sticky content. Spans 100% width across the bottom of the Container
 * @slot fixed-content-left - Named slot for fixed/sticky content. Spans top to bottom on the left side of the Container
 * @slot fixed-content-right - Named slot for fixed/sticky content. Spans top to bottom on the right side of the Container
 *
 */
@Component({
  tag: 'fluid-container',
  styleUrl: 'fluid-container.scss',
  shadow: true,
})
export class FluidContainer implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Object that stores calculated values for inline styles.
   */
  private _calcValues: FluidKeyedObject<string>;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidContainerElement;

  // ================================================================ //
  // -- States

  /**
   * States that track whether content is provided to each Slot.
   * Each Slot will only be rendered if content is provided.
   */
  @State() _renderSlotTop: boolean;
  @State() _renderSlotBottom: boolean;
  @State() _renderSlotLeft: boolean;
  @State() _renderSlotRight: boolean;

  // ================================================================ //
  // -- Props

  /**
   * Theme settings the component should use.
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If true, Container will render as inline-block element. If false,
   * Container will render as block element.
   */
  @Prop({ mutable: true }) inline = false;

  /**
   * The width of the Container. FluidSize values equate to preset
   * percentages, decimal numbers between 0 and 1 equate to percent
   * values, whole numbers greater than 1 equate to pixel values, and
   * strings are outputted directly as inline style values (NOTE: strings
   * should contain valid CSS units - i.e. 'px', '%', 'em', 'vw', etc.).
   */
  @Prop({ mutable: true }) width: FluidStyleSize;

  /**
   * The minimum width of the Container. FluidSize values equate to preset
   * percentages, decimal numbers between 0 and 1 equate to percent
   * values, whole numbers greater than 1 equate to pixel values, and
   * strings are outputted directly as inline style values (NOTE: strings
   * should contain valid CSS units - i.e. 'px', '%', 'em', 'vw', etc.).
   */
  @Prop({ mutable: true }) minWidth: FluidStyleSize;

  /**
   * The maximum width of the Container. FluidSize values equate to preset
   * percentages, decimal numbers between 0 and 1 equate to percent
   * values, whole numbers greater than 1 equate to pixel values, and
   * strings are outputted directly as inline style values (NOTE: strings
   * should contain valid CSS units - i.e. 'px', '%', 'em', 'vw', etc.).
   */
  @Prop({ mutable: true }) maxWidth: FluidStyleSize;

  /**
   * The height of the Container. FluidSize values equate to preset
   * percentages, decimal numbers between 0 and 1 equate to percent
   * values, whole numbers greater than 1 equate to pixel values, and
   * strings are outputted directly as inline style values (NOTE: strings
   * should contain valid CSS units - i.e. 'px', '%', 'em', 'vh', etc.).
   */
  @Prop({ mutable: true }) height: FluidStyleSize;

  /**
   * The minimum height of the Container. FluidSize values equate to preset
   * percentages, decimal numbers between 0 and 1 equate to percent
   * values, whole numbers greater than 1 equate to pixel values, and
   * strings are outputted directly as inline style values (NOTE: strings
   * should contain valid CSS units - i.e. 'px', '%', 'em', 'vh', etc.).
   */
  @Prop({ mutable: true }) minHeight: FluidStyleSize;

  /**
   * The maximum height of the Container. FluidSize values equate to preset
   * percentages, decimal numbers between 0 and 1 equate to percent
   * values, whole numbers greater than 1 equate to pixel values, and
   * strings are outputted directly as inline style values (NOTE: strings
   * should contain valid CSS units - i.e. 'px', '%', 'em', 'vh', etc.).
   */
  @Prop({ mutable: true }) maxHeight: FluidStyleSize;

  /**
   * How the Container should handle horizontal content overflow. NOTE: setting
   * this Prop to 'hidden' or 'scroll' WILL cause absolutely-position elements
   * (e.g. FLUID Popover, FLUID Dropdown Button, etc.) that overflow the container
   * bounds to be truncated.
   */
  @Prop({ mutable: true }) overflowX: FluidOverflow;

  /**
   * How the Container should handle vertical content overflow. NOTE: setting
   * this Prop to 'hidden' or 'scroll' WILL cause absolutely-position elements
   * (e.g. FLUID Popover, FLUID Dropdown Button, etc.) that overflow the container
   * bounds to be truncated.
   */
  @Prop({ mutable: true }) overflowY: FluidOverflow;

  /**
   * Padding applied to the body of the Container. FluidSize values equate
   * to preset pixel values, decimal numbers between 0 and 1 equate to percent
   * values, whole numbers greater than 1 equate to pixel values, and
   * strings are outputted directly as inline style values (NOTE: strings
   * should contain valid CSS units - i.e. 'px', '%', 'em', 'vw', etc.). Strings
   * can contain CSS shorthand to set padding on individual sides of the Container
   * (e.g. '1em 2em 3em 4em' would set 1em padding on Container top, 2em padding on
   * Container right, 3em padding on Container bottom, & 4em padding on Container left).
   */
  @Prop({ mutable: true }) padding: Exclude<FluidStyleSize, FluidSize.FULL>;

  /**
   * Margin applied to Container element. FluidSize values equate to preset
   * pixel values, decimal numbers between -1 and 1 equate to negative/positive
   * percent values, whole numbers less than -1 or greater than 1 equate to
   * negative/positive pixel values, and strings are outputted directly as inline style
   * values (NOTE: strings should contain valid CSS units - i.e. 'px', '%', 'em', 'vw',
   * etc.). Strings can contain CSS shorthand to set margin on individual sides of the
   * Container (e.g. '1em 2em 3em 4em' would set 1em margin on Container top, 2em margin on
   * Container right, 3em margin on Container bottom, & 4em margin on Container left).
   */
  @Prop({ mutable: true }) margin: Exclude<FluidStyleSize, FluidSize.FULL>;

  /**
   * Alignment of text & content within the Container. This applies to text elements
   * (i.e. 'p', 'span', 'fluid-text', etc.), inline elements, & inline-block elements.
   */
  @Prop({ mutable: true }) textAlign: FluidAlignment = FluidAlignment.LEFT;

  /**
   * Sets the background color of the Container.
   */
  @Prop({ mutable: true }) backgroundColor: FluidBackgroundColor =
    FluidBackgroundColor.TRANSPARENT;

  // ================================================================ //
  // -- Testing Enablement

  /**
   * When supplied, will be prepended on each inner element of the container in order
   * to help facilitate testing.
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: findSlotContent - determines if 'fixed-content' slot(s) should be rendered
   * @action: setInlineStyleValues - sets Container styles based on Prop values
   * @action: watchChildContent - watch for 'fixed-content' slots to be added/removed
   */
  componentWillLoad() {
    this.findSlotContent();
    this.setInlineStyleValues();
    Utils.watchChildContent(this.host, () => this.findSlotContent());
  }

  /**
   * Called when component will be updated due to @Prop or @State change.
   * @action: setInlineStyleValues - sets Container styles based on Prop values
   */
  componentWillUpdate() {
    this.setInlineStyleValues();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Determines if slotted content is provided to each "fixed-content..."
   * Slot & sets each corresponding @State appropriately. Ultimately,
   * decides if each "fixed-content..." Slot is rendered or not.
   */
  findSlotContent(): void {
    this._renderSlotTop = !!this.host.querySelector(
      '[slot="fixed-content-top"]'
    );
    this._renderSlotBottom = !!this.host.querySelector(
      '[slot="fixed-content-bottom"]'
    );
    this._renderSlotLeft = !!this.host.querySelector(
      '[slot="fixed-content-left"]'
    );
    this._renderSlotRight = !!this.host.querySelector(
      '[slot="fixed-content-right"]'
    );
  }

  /**
   * Provides fallback 'height' value to enable vertical scrolling when
   * 'overflowY' & 'maxHeight' Props ARE provided, but 'height' Prop is NOT.
   * This fallback is necessary because of a CSS technicality that prevents
   * vertical scrolling when a parent element has 'max-height' instead of
   * 'height' set, and the 'overflow' property is set on a CHILD element
   * instead of the parent itself.
   */
  overflowHeightFallback(): string {
    if (!!this.height || !this.overflowY || !this.maxHeight) return;
    return FULL_HEIGHT_DEFAULT;
  }

  /**
   * Sets calculated inline style values for each Prop provided within
   * '_calcValues'. Also sets fallback 'height' value if appropriate (see
   * 'overflowHeightFallback()' for more information).
   */
  setInlineStyleValues(): void {
    this._calcValues = {
      width: parseSizeValue(this.width),
      minWidth: parseSizeValue(this.minWidth),
      maxWidth: parseSizeValue(this.maxWidth),
      height: parseSizeValue(this.height) ?? this.overflowHeightFallback(),
      minHeight: parseSizeValue(this.minHeight),
      maxHeight: parseSizeValue(this.maxHeight),
      padding: parseSizeValue(this.padding),
      margin: parseSizeValue(this.margin),
    };
  }

  /**
   * Returns true if parameter is a value of FluidSize enum, false if not.
   * @param value - value to check against FluidSize enum
   */
  isFluidSize(value: any): boolean {
    return Utils.isFluidEnum<FluidSize>(value, FluidSize);
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host
        class={{
          'fluid-container--inline': this.inline,
          [`width-${this.width}`]: this.isFluidSize(this.width),
          [`min-width-${this.minWidth}`]: this.isFluidSize(this.minWidth),
          [`max-width-${this.maxWidth}`]: this.isFluidSize(this.maxWidth),
          [`height-${this.height}`]: this.isFluidSize(this.height),
          [`min-height-${this.minHeight}`]: this.isFluidSize(this.minHeight),
          [`max-height-${this.maxHeight}`]: this.isFluidSize(this.maxHeight),
          [`margin-${this.margin}`]: this.isFluidSize(this.margin),
        }}
        data-test-id={`${this.identifier}-fluid-container-inline`}
        style={{
          width: this._calcValues.width,
          'min-width': this._calcValues.minWidth,
          'max-width': this._calcValues.maxWidth,
          height: this._calcValues.height,
          'min-height': this._calcValues.minHeight,
          'max-height': this._calcValues.maxHeight,
          margin: this._calcValues.margin,
        }}>
        <div
          class={{
            [`fluid-container theme-${this.theme}`]: true,
            [`text-align-${this.textAlign}`]: true,
            [`background-color-${this.backgroundColor}`]: true,
            [`padding-${this.padding}`]: this.isFluidSize(this.padding),
            [`overflow-x-${this.overflowX}`]: !!this.overflowX,
            [`overflow-y-${this.overflowY}`]: !!this.overflowY,
          }}
          data-test-id={`${this.identifier}-fluid-container`}
          style={{
            padding: this._calcValues.padding,
          }}>
          <slot data-test-id={`${this.identifier}-fluid-container-slot`} />
        </div>
        <ContainerNamedSlots host={this} identifier={this.identifier} />
      </Host>
    );
  }
}
